import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { fmtCHF } from "../lib/constants";
import { useLang } from "../contexts/LanguageContext";
import { TrendingDown, TrendingUp, Minus, Loader2, Sparkles, Info } from "lucide-react";

/**
 * Live "fair price" indicator that compares the entered price against
 * similar active CarHive listings. Debounced 600ms.
 */
export default function PriceIndicator({ brand, model, year, mileageKm, powerHp, priceChf, excludeId, compact = false }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  // Only fetch when basics are present; debounce all rapid changes
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!brand || !model || !priceChf || Number(priceChf) <= 0) {
      setData(null); setLoading(false); setError(null);
      return;
    }
    setLoading(true); setError(null);
    timer.current = setTimeout(async () => {
      try {
        const res = await api.priceSuggestion({
          brand,
          model,
          year: Number(year) || undefined,
          mileage_km: Number(mileageKm) || undefined,
          power_hp: Number(powerHp) || undefined,
          price_chf: Number(priceChf),
          exclude_id: excludeId || undefined,
        });
        setData(res);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    }, 600);
    return () => clearTimeout(timer.current);
  }, [brand, model, year, mileageKm, powerHp, priceChf, excludeId]);

  // Empty placeholder when prerequisites missing
  if (!brand || !model || !priceChf || Number(priceChf) <= 0) {
    if (compact) return null;
    return (
      <div className="flex items-center gap-2 rounded-lg border border-dashed border-zinc-800 bg-zinc-950/40 px-4 py-3 text-xs text-zinc-500" data-testid="price-indicator-waiting">
        <Info className="h-3.5 w-3.5" />
        <span>{t.price?.waiting || "Marke, Modell & Preis eingeben für Marktvergleich"}</span>
      </div>
    );
  }

  if (loading || (!data && !error)) {
    if (compact) {
      return (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-zinc-800/60 bg-zinc-950/40 px-3 py-2 text-[11px] text-zinc-500" data-testid="price-indicator-loading">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>{t.price?.checking || "Vergleiche…"}</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-3 text-xs text-zinc-400" data-testid="price-indicator-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-red-500" />
        <span>{t.price?.checking || "Vergleiche…"}</span>
      </div>
    );
  }

  if (error) {
    if (compact) return null;
    return (
      <div className="rounded-lg border border-red-900/50 bg-red-950/20 px-4 py-3 text-xs text-red-300" data-testid="price-indicator-error">
        {t.price?.error || "Vergleich fehlgeschlagen."}
      </div>
    );
  }

  if (data?.tier === "insufficient" || data?.sample_count < 3) {
    if (compact) return null; // hide silently in buyer's compact view
    return (
      <div className="flex items-start gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-3 text-xs text-zinc-400" data-testid="price-indicator-insufficient">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-zinc-500" />
        <span>{t.price?.insufficient || "Zu wenige Vergleichsfahrzeuge."} ({data?.sample_count || 0} {t.price?.sampleN || "Vergleichswerte"})</span>
      </div>
    );
  }

  return <PriceCard data={data} t={t} compact={compact} />;
}

function PriceCard({ data, t, compact = false }) {
  const verdictMap = {
    very_good: { label: t.price?.verdictVeryGood || "Sehr guter Preis", buyerMsg: t.price?.buyerVeryGood || "Klar unter Marktdurchschnitt — schneller Deal!", color: "emerald", Icon: TrendingDown, glow: "rgba(16,185,129,0.4)" },
    good:      { label: t.price?.verdictGood || "Guter Preis",          buyerMsg: t.price?.buyerGood || "Attraktiv für Käufer — fairer Marktpreis.", color: "lime",    Icon: TrendingDown, glow: "rgba(132,204,22,0.4)" },
    market:    { label: t.price?.verdictMarket || "Marktgerecht",        buyerMsg: t.price?.buyerMarket || "Im typischen Marktbereich für vergleichbare Fahrzeuge.", color: "sky",     Icon: Minus,        glow: "rgba(14,165,233,0.4)" },
    above:     { label: t.price?.verdictAbove || "Über Durchschnitt",    buyerMsg: t.price?.buyerAbove || "Etwas über dem Markt — Verhandlungsspielraum möglich.", color: "amber",   Icon: TrendingUp,   glow: "rgba(245,158,11,0.4)" },
    high:      { label: t.price?.verdictHigh || "Deutlich zu hoch",      buyerMsg: t.price?.buyerHigh || "Deutlich über vergleichbaren Inseraten.", color: "red",     Icon: TrendingUp,   glow: "rgba(239,68,68,0.5)" },
  };
  const v = verdictMap[data.verdict] || verdictMap.market;
  const colorClasses = {
    emerald: { bar: "bg-emerald-500", text: "text-emerald-400", border: "border-emerald-500/40", bg: "bg-emerald-500/10" },
    lime:    { bar: "bg-lime-500",    text: "text-lime-400",    border: "border-lime-500/40",    bg: "bg-lime-500/10" },
    sky:     { bar: "bg-sky-500",     text: "text-sky-400",     border: "border-sky-500/40",     bg: "bg-sky-500/10" },
    amber:   { bar: "bg-amber-500",   text: "text-amber-400",   border: "border-amber-500/40",   bg: "bg-amber-500/10" },
    red:     { bar: "bg-red-500",     text: "text-red-400",     border: "border-red-500/40",     bg: "bg-red-500/10" },
  }[v.color];

  // Position user's price on the p10..p90 scale (clamped 0..1)
  const lo = data.p10, hi = data.p90;
  const pct = hi > lo ? Math.max(0, Math.min(1, (data.user_price - lo) / (hi - lo))) : 0.5;
  const tierLabelKey = `tier${data.tier.replace(/(^.|_(.))/g, (_, a, b) => (b || a).toUpperCase())}`;
  const tierLabel = t.price?.[tierLabelKey] || data.tier;
  const delta = data.delta_pct;

  if (compact) {
    return (
      <div
        data-testid="price-indicator-card"
        data-verdict={data.verdict}
        className={`relative overflow-hidden rounded-lg border ${colorClasses.border} ${colorClasses.bg} px-3 py-3 backdrop-blur-sm transition-all duration-500 animate-in fade-in slide-in-from-bottom-1`}
        style={{ boxShadow: `0 0 20px -10px ${v.glow}` }}
      >
        <div className="flex items-center gap-2.5">
          <div className={`grid h-7 w-7 flex-shrink-0 place-items-center rounded-full ${colorClasses.bar} text-white shadow-md`}>
            <v.Icon className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${colorClasses.text}`} data-testid="price-verdict-label">
                {v.label}
              </span>
              {delta !== null && delta !== undefined && (
                <span className={`tabular-nums text-sm font-bold ${colorClasses.text}`} data-testid="price-delta">
                  {delta > 0 ? "+" : ""}{delta}%
                </span>
              )}
            </div>
            <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-zinc-400">{v.buyerMsg}</p>
          </div>
        </div>

        {/* Mini-Skala */}
        <div className="mt-2.5">
          <div className="relative h-1.5 overflow-hidden rounded-full bg-zinc-900">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-sky-500 to-red-500 opacity-30" />
            <div
              className="absolute inset-y-0 bg-white/15"
              style={{
                left: `${Math.max(0, ((data.p25 - lo) / (hi - lo)) * 100)}%`,
                right: `${Math.max(0, 100 - ((data.p75 - lo) / (hi - lo)) * 100)}%`,
              }}
            />
            <div
              className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-zinc-950 transition-all duration-700"
              style={{ left: `${pct * 100}%`, boxShadow: `0 0 10px ${v.glow}` }}
              data-testid="price-marker"
            />
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-zinc-500">
            <span>{t.price?.median || "Median"}: <span className="text-zinc-300 tabular-nums">{fmtCHF(data.median_price)}</span></span>
            <span className="text-zinc-500">{data.sample_count} {t.price?.sampleN || "Vergleiche"}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="price-indicator-card"
      data-verdict={data.verdict}
      className={`group relative overflow-hidden rounded-xl border ${colorClasses.border} ${colorClasses.bg} px-4 py-4 backdrop-blur-sm transition-all duration-500 sm:px-5`}
      style={{ boxShadow: `0 0 30px -12px ${v.glow}` }}
    >
      {/* Top row: verdict pill + delta */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className={`grid h-9 w-9 place-items-center rounded-full ${colorClasses.bar} text-white shadow-lg`}>
            <v.Icon className="h-4 w-4" />
          </div>
          <div>
            <div className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${colorClasses.text}`} data-testid="price-verdict-label">
              {v.label}
            </div>
            <div className="mt-0.5 text-[11px] text-zinc-500">
              {data.sample_count} {t.price?.sampleN || "Vergleichswerte"} · {tierLabel}
            </div>
          </div>
        </div>
        {delta !== null && delta !== undefined && (
          <div className={`text-right ${colorClasses.text}`} data-testid="price-delta">
            <div className="font-display text-2xl font-bold tabular-nums">
              {delta > 0 ? "+" : ""}{delta}%
            </div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">
              {delta < -0.5 ? (t.price?.deltaBelow || "unter Median")
                : delta > 0.5 ? (t.price?.deltaAbove || "über Median")
                : (t.price?.deltaMatch || "auf Median-Niveau")}
            </div>
          </div>
        )}
      </div>

      {/* Visual scale */}
      <div className="mt-4">
        <div className="relative h-2 overflow-hidden rounded-full bg-zinc-900">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-sky-500 to-red-500 opacity-30" />
          <div
            className="absolute inset-y-0 bg-white/15"
            style={{
              left: `${Math.max(0, ((data.p25 - lo) / (hi - lo)) * 100)}%`,
              right: `${Math.max(0, 100 - ((data.p75 - lo) / (hi - lo)) * 100)}%`,
            }}
          />
          <div
            className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-zinc-950 shadow-lg transition-all duration-700"
            style={{ left: `${pct * 100}%`, boxShadow: `0 0 12px ${v.glow}` }}
            data-testid="price-marker"
          />
        </div>
        <div className="mt-1.5 flex justify-between text-[10px] uppercase tracking-wider text-zinc-500">
          <span>{fmtCHF(data.min_price)}</span>
          <span className={colorClasses.text}>{t.price?.you || "Dein Preis"}: {fmtCHF(data.user_price)}</span>
          <span>{fmtCHF(data.max_price)}</span>
        </div>
      </div>

      {/* Stats row */}
      <div className="mt-4 grid grid-cols-3 gap-2 border-t border-zinc-800/80 pt-3">
        <Stat label={t.price?.median || "Median"} value={fmtCHF(data.median_price)} highlight />
        <Stat label={t.price?.avg || "Durchschnitt"} value={fmtCHF(data.avg_price)} />
        <Stat label={t.price?.range || "Markt-Range"} value={`${fmtCHF(data.p25)}–${fmtCHF(data.p75)}`} small />
      </div>
    </div>
  );
}

function Stat({ label, value, highlight, small }) {
  return (
    <div className="text-left">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`mt-0.5 tabular-nums ${highlight ? "text-white" : "text-zinc-300"} ${small ? "text-xs" : "text-sm"} font-semibold`}>
        {value}
      </div>
    </div>
  );
}
